import { oc } from "@orpc/contract";
import { z } from "zod";

// --- Shared schemas ---

const webhookSubscriptionSchema = z.object({
  mailboxUpn: z.string(),
  subscriptionId: z.string(),
  expirationDateTime: z.string(),
  resource: z.string().optional(),
  clientState: z.string().nullish(),
  applicationId: z.string().optional(),
  changeType: z.string().optional(),
});

const webhookStatusSchema = z.object({
  isActive: z.boolean(),
  subscriptions: z.array(webhookSubscriptionSchema),
  lastChecked: z.date(),
});

const mailboxPermissionSchema = z.enum(["read", "write"]);

const mailboxSchema = z.object({
  id: z.uuid(),
  upn: z.string(),
  name: z.string().nullable(),
  enabled: z.boolean(),
  permission: mailboxPermissionSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});

const emailIngestConfigSummarySchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  enabled: z.boolean(),
  tenantId: z.string().nullable(),
  clientId: z.string().nullable(),
  hasClientSecret: z.boolean(),
  clientSecretExpiresAt: z.string().nullable(),
  mailboxes: z.array(mailboxSchema),
  webhookStatus: webhookStatusSchema.optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

const webhookSubscriptionFailureSchema = z.object({
  mailboxUpn: z.string(),
  error: z.string(),
});

// --- Contracts ---

const listContract = oc.output(z.array(emailIngestConfigSummarySchema)).route({
  method: "GET",
  summary: "List email ingest configurations",
  description:
    "Returns the organization's Microsoft 365 mailbox ingest app registrations with their mailboxes, secret expiry, and live webhook subscription status for enabled configs.",
});

const getContract = oc
  .input(z.object({ appRegistrationId: z.uuid() }))
  .output(emailIngestConfigSummarySchema)
  .route({
    method: "GET",
    summary: "Get email ingest configuration",
    description:
      "Returns one mailbox ingest app registration by ID with its mailboxes, secret expiry, and current webhook subscription status.",
  });

const createContract = oc
  .input(
    z.object({
      name: z.string().trim().min(1).max(255),
      tenantId: z.string().trim().min(1),
      clientId: z.string().trim().min(1),
      clientSecret: z.string().trim().min(1),
      clientSecretExpiresInDays: z.number().int().positive().nullish(),
    }),
  )
  .output(emailIngestConfigSummarySchema)
  .route({
    summary: "Create email ingest configuration",
    description:
      "Saves a new, initially disabled Microsoft 365 app registration (tenant, client ID, encrypted secret and expiry) used to ingest email from organization mailboxes.",
  });

const updateContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      name: z.string().trim().min(1).max(255).optional(),
      enabled: z.boolean().optional(),
      // Credential rotation — Azure's "Secret ID" is not accepted here; callers
      // must pass the secret *Value* shown once when the client secret is created.
      tenantId: z.string().trim().min(1).optional(),
      clientId: z.string().trim().min(1).optional(),
      clientSecret: z.string().trim().min(1).optional(),
      clientSecretExpiresInDays: z.number().int().positive().nullish(),
    }),
  )
  .output(
    emailIngestConfigSummarySchema.extend({
      /**
       * Present when credential rotation / enable attempted webhook
       * re-subscribe and one or more mailboxes failed. Credentials are still
       * persisted; callers should surface this instead of treating the update
       * as fully healthy.
       */
      subscriptionFailures: z
        .array(webhookSubscriptionFailureSchema)
        .optional(),
    }),
  )
  .route({
    summary: "Update email ingest configuration",
    description:
      "Changes name, enabled state or app credentials. Enabling or rotating the secret re-subscribes mailbox webhooks; disabling unsubscribes them. Returns any per-mailbox subscription failures.",
  });

const deleteContract = oc
  .input(z.object({ appRegistrationId: z.uuid() }))
  .output(z.object({ success: z.boolean() }))
  .route({
    summary: "Delete email ingest configuration",
    description:
      "Unsubscribes all mailbox webhooks for the app registration, then deletes the configuration and its mailboxes.",
  });

const toggleUpnContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      mailboxId: z.uuid(),
      enabled: z.boolean(),
    }),
  )
  .output(mailboxSchema)
  .route({
    summary: "Enable or disable an ingest mailbox",
    description:
      "Turns email ingestion for one mailbox on or off, subscribing or unsubscribing its new-mail webhook for read mailboxes. Returns the updated mailbox.",
  });

const addUpnContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      upn: z.string().email(),
      name: z.string().trim().nullish(),
      permission: mailboxPermissionSchema,
    }),
  )
  .output(mailboxSchema)
  .route({
    summary: "Add a mailbox to email ingest",
    description:
      "Adds a mailbox address with read or write permission to an ingest configuration; read mailboxes on an enabled config get a new-mail webhook subscription.",
  });

const updateUpnContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      mailboxId: z.uuid(),
      upn: z.string().email().optional(),
      name: z.string().trim().nullish(),
    }),
  )
  .output(mailboxSchema)
  .route({
    summary: "Update an ingest mailbox address or name",
    description:
      "Changes a mailbox's email address or display name; changing the address of an active read mailbox moves its webhook subscription to the new address.",
  });

const removeUpnContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      mailboxId: z.uuid(),
    }),
  )
  .output(z.object({ success: z.boolean() }))
  .route({
    summary: "Remove a mailbox from email ingest",
    description:
      "Unsubscribes the mailbox's webhook (read mailboxes) and deletes it from the ingest configuration.",
  });

const refreshWebhooksContract = oc
  .input(z.object({ appRegistrationId: z.uuid() }))
  .output(webhookStatusSchema)
  .route({
    summary: "Refresh all mailbox webhook subscriptions",
    description:
      "Unsubscribes every webhook for the ingest configuration, re-subscribes all enabled read mailboxes, and returns the resulting webhook status.",
  });

const refreshUpnContract = oc
  .input(z.object({ appRegistrationId: z.uuid(), mailboxId: z.uuid() }))
  .output(z.object({ success: z.boolean() }))
  .route({
    summary: "Refresh one mailbox webhook subscription",
    description:
      "Re-creates the new-mail webhook subscription for a single mailbox in an ingest configuration.",
  });

const lookbackRequeueMailboxSchema = z.object({
  mailboxUpn: z.string(),
  emailsFound: z.number().int().nonnegative(),
  emailsQueued: z.number().int().nonnegative(),
  emailsSkipped: z.number().int().nonnegative(),
  partialChunks: z.number().int().nonnegative(),
  errors: z.array(z.string()),
});

const requeueMissedEmailsContract = oc
  .input(
    z.object({
      appRegistrationId: z.uuid(),
      /** Hours to look back from now (exclusive of the trailing exclude window). */
      lookbackHours: z.number().positive().max(168),
    }),
  )
  .output(
    z.object({
      status: z.enum(["completed", "completed_with_errors", "busy", "noop"]),
      organizationId: z.uuid(),
      appRegistrationId: z.uuid(),
      lookbackHours: z.number(),
      chunkHours: z.number(),
      chunksTotal: z.number().int().nonnegative(),
      chunksCompleted: z.number().int().nonnegative(),
      emailsFound: z.number().int().nonnegative(),
      /** Missed emails published back onto ms-graph-email-ingest. */
      emailsQueued: z.number().int().nonnegative(),
      /**
       * Same as emailsQueued — explicit alias for UI/ops copy
       * ("N missed emails were requeued").
       */
      missedEmailsRequeued: z.number().int().nonnegative(),
      emailsSkipped: z.number().int().nonnegative(),
      partialChunks: z.number().int().nonnegative(),
      durationMs: z.number().int().nonnegative(),
      mailboxes: z.array(lookbackRequeueMailboxSchema),
      message: z.string().optional(),
    }),
  )
  .route({
    summary: "Requeue missed inbound emails",
    description:
      "Scans the configuration's mailboxes over a lookback window (up to 168 hours) and requeues emails never ingested. Returns per-mailbox counts found, queued and skipped.",
  });

export const emailIngestConfigsContracts = {
  list: listContract,
  get: getContract,
  create: createContract,
  update: updateContract,
  delete: deleteContract,
  toggleUpn: toggleUpnContract,
  addUpn: addUpnContract,
  updateUpn: updateUpnContract,
  removeUpn: removeUpnContract,
  refreshWebhooks: refreshWebhooksContract,
  refreshUpn: refreshUpnContract,
  requeueMissedEmails: requeueMissedEmailsContract,
};

export type EmailIngestConfigsContracts = typeof emailIngestConfigsContracts;
